import has from "lodash-es/has";
import intersection from "lodash-es/intersection";
import take from "lodash-es/take";
import { makeAutoObservable } from "mobx";
import { ACCEPT_FALLBACK_HEADER, apiClient } from "../common/apiClient";
import { logger } from "../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../common/platformRoutes";
import type { BaseStoreType } from "../common/types/BaseStoreType";
import {
   type AsyncData,
   initializeWithDefaultData,
   isAbortError,
   setAsDataAvailable,
   setAsErrorOccured,
   setAsWaitingForData
} from "../common/utils/asyncDataUtils";
import theme from "../themes/theme";
import authStore from "./auth/authStore";
import productStore from "./product/productStore";

type RecommendationBase = {
   reason: string;
   producedBy: string;
   createdAt: string;
};

type Recommendation = RecommendationBase & {
   skus: Suggestion[];
};

type RecommendationBySource = {
   [source: string]: Recommendation;
};

type Suggestion = {
   sku: string;
   confidence: number;
};

type ML001Recommendation = RecommendationBase & {
   suggestions: SuggestionsBySource;
};

type SuggestionsBySource = {
   [source: string]: ML001Suggestion[];
};

type ML001Suggestion = {
   sku: string;
   confidence: number;
   type: string;
};

export type ContractualRecommendationResponse = {
   foundContractualData: boolean;
   customerNumber: string;
   relevantChainIds: string[];
   contractualObligatedProducts: ContractualObligatedProduct[];
   voluntaryProducts: string[];
   shouldBuy: string[];
};

export type ContractualObligatedProduct = {
   sku: string;
   lastPurchase: Date | null;
};

const getSkusFromRecommendation = (recommendation: ML001Recommendation, type: string): string[] => {
   if (!has(recommendation.suggestions, type)) {
      return [];
   }

   return recommendation.suggestions[type].map((suggestion) => suggestion.sku);
};

const adjustForAvailableProducts = (recommendation: Recommendation | ML001Recommendation | undefined): Recommendation => {
   if (!recommendation) {
      return {
         reason: "Ingen tilgjengelig anbefalinger",
         skus: [],
         producedBy: "",
         createdAt: ""
      };
   }

   let skus = [];
   if ("suggestions" in recommendation) {
      // Handle old backend with split product suggestions
      const newProducts = intersection(productStore.assortment, getSkusFromRecommendation(recommendation, "Nyhet"));
      const focusProducts = intersection(productStore.assortment, getSkusFromRecommendation(recommendation, "Fokus"));
      const normalProducts = intersection(productStore.assortment, getSkusFromRecommendation(recommendation, "Fordelingsnøkkel"));

      const newProductsCount = Math.min(1, newProducts.length);
      const focusProductsCount = Math.min(2, focusProducts.length);
      const normalProductsCount = Math.min(6 - newProductsCount - focusProductsCount);

      logger.log(
         "recommendations",
         `Using ${newProductsCount} new products, ${focusProductsCount} focus products and ${normalProductsCount} other products`
      );

      skus = [
         ...take(normalProducts, normalProductsCount),
         ...take(focusProducts, focusProductsCount),
         ...take(newProducts, newProductsCount)
      ].map((sku) => ({ sku, confidence: 1 }));
   } else {
      // Handle new backend, with combined product suggestions and intergrated confidence
      skus = recommendation.skus;
   }

   return {
      reason: recommendation.reason,
      skus: skus,
      producedBy: recommendation.producedBy,
      createdAt: recommendation.createdAt
   };
};

type RecommendationStoreType = BaseStoreType & {
   recommendations: AsyncData<RecommendationBySource | null>;
   contractualRecommendations: AsyncData<ContractualRecommendationResponse | null>;

   getRecommendations(): void;
   sendRecommendationFeedback(serviceId: string, sku: string, action: string): void;

   getContractualRecommendations(): void;
};

const recommendationStore: RecommendationStoreType = makeAutoObservable({
   recommendations: initializeWithDefaultData(null),
   contractualRecommendations: initializeWithDefaultData(null),

   getRecommendations: () => {
      const abortController = setAsWaitingForData(recommendationStore.recommendations);
      const { currentCompany: customerNumber } = authStore;
      if (!customerNumber) {
         logger.warn("recommendations", "Need to be logged in to fetch recommendations");
         return;
      }
      const url = resolvePlatformRequestUrl(platformRoutes.recommendations, { customerNumber });
      void apiClient(url, authStore.getSessionToken())
         .headers(ACCEPT_FALLBACK_HEADER)
         .signal(abortController)
         .get()
         .json(async (res: { [source: string]: ML001Recommendation }) => {
            setAsDataAvailable(recommendationStore.recommendations, {
               AI1: adjustForAvailableProducts(res.AI1),
               AI2: adjustForAvailableProducts(res.AI2),
               AI3: adjustForAvailableProducts(res.AI3)
            });
            logger.log("recommendations", "Custom Recommendations loaded", recommendationStore.recommendations.data);
         })
         .catch((err) => {
            if (!isAbortError(err)) {
               logger.warn("recommendations", "Unable to load recommendations: ", err.message);
               setAsErrorOccured(recommendationStore.recommendations, `${err}`);
            }
         });
   },

   sendRecommendationFeedback: (serviceId, sku, action) => {
      const { currentCompany: customerNumber } = authStore;
      if (!customerNumber) {
         logger.warn("recommendations", "Need to be logged in to send recommendation feedback");
         return;
      }
      logger.log("recommendations", `Sending feedback for recommendation service ${serviceId} ${action} with SKU ${sku}`);
      const url = resolvePlatformRequestUrl(platformRoutes.recommendationFeedback, { customerNumber });
      void apiClient(url, authStore.getSessionToken())
         .query({
            serviceId,
            sku,
            action
         })
         .post()
         .json();
   },

   getContractualRecommendations: () => {
      const abortController = setAsWaitingForData(recommendationStore.contractualRecommendations);
      const m3CompanyNumber = theme.m3CompanyNumber;
      const { currentCompany: customerNumber } = authStore;
      if (!customerNumber) {
         logger.warn("recommendations", "Need to be logged in to fetch contractual recommendations");
         return;
      }
      const url = resolvePlatformRequestUrl(platformRoutes.recommendationsContractual, {
         m3CompanyNumber,
         customerNumber
      });
      void apiClient(url, authStore.getSessionToken())
         .headers(ACCEPT_FALLBACK_HEADER)
         .signal(abortController)
         .get()
         .json(async (res: ContractualRecommendationResponse) => {
            setAsDataAvailable(recommendationStore.contractualRecommendations, res);
            logger.log(
               "recommendations",
               "Contractual Recommendations loaded",
               recommendationStore.contractualRecommendations.data
            );
         })
         .catch((err) => {
            if (!isAbortError(err)) {
               logger.warn("recommendations", "Unable to load contractual recommendations: ", err.message);
               setAsErrorOccured(recommendationStore.contractualRecommendations, `${err}`);
            }
         });
   },
   clearCompanySpecificData: () => {
      recommendationStore.recommendations = initializeWithDefaultData(null);
   }
} satisfies RecommendationStoreType);

export default recommendationStore;
